/** Provider-neutral presentation values used by conversation and tool components. */
export type ToolPart = {
  kind: 'tool'
  toolUseId: string
  toolName: string
  toolKind: 'builtin' | 'server' | 'mcp'
  mcpServer?: string
  args: Record<string, unknown>
  argsText: string
  argsSettled: boolean
  result?: { text: string; isError: boolean; raw: unknown }
  progress?: {
    elapsedSeconds: number
    heartbeat: boolean
    taskId?: string
    subagentType?: string
  }
  denial?: { message: string; reason?: string }
  notes: {
    level: 'info' | 'notice' | 'suggestion' | 'warning'
    content: string
    preventsContinuation: boolean
  }[]
  summarizedBy?: string
}

/** A presentation identity, independent of a message's conversational role. */
export type MessageActor = {
  kind: 'user' | 'app' | 'agent' | 'system'
  id: string
  name?: string
  avatarUrl?: string
}

/** Read optional actor metadata without letting malformed frames break replay. */
export function readMessageActor(value: unknown): MessageActor | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    return undefined
  const actor = value as Record<string, unknown>
  if (
    !['user', 'app', 'agent', 'system'].includes(String(actor.kind)) ||
    typeof actor.id !== 'string' ||
    !actor.id.trim()
  )
    return undefined
  return {
    kind: actor.kind as MessageActor['kind'],
    id: actor.id,
    name: typeof actor.name === 'string' ? actor.name : undefined,
    avatarUrl:
      typeof actor.avatarUrl === 'string' ? actor.avatarUrl : undefined,
  }
}

/** Only merge messages whose actor identity and presentation match. */
export function sameMessageActor(a?: MessageActor, b?: MessageActor): boolean {
  return (
    a?.kind === b?.kind &&
    a?.id === b?.id &&
    a?.name === b?.name &&
    a?.avatarUrl === b?.avatarUrl
  )
}
